import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const SignInLayer = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isOtpPhase, setIsOtpPhase] = useState(false);
  const [timer, setTimer] = useState(30);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
  
    try {
      const baseUrl = process.env.REACT_APP_API;
  
      if (!baseUrl) {
        throw new Error("Base API URL is not defined in the environment file!");
      }
  
      const apiUrl = `${baseUrl}/signinDirectorAdmin`;
  
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });
  
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        throw new Error("Invalid JSON response from the server.");
      }
  
      if (!response.ok || !data.success) {
        const errorMessage = data?.message || "Login failed. Please check your credentials.";
        throw new Error(errorMessage);
      }
      
      setIsOtpPhase(true); 
      setTimer(30); 
      setIsResendDisabled(true);
      toast.success(data.message || "Login successful! OTP sent.");
    } catch (error) {
      console.error("Login failed:", error.message);
      toast.error(error.message);
    }
  };
  
  const handleResendOtp = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API;
  
      if (!baseUrl) {
        throw new Error("Base API URL is not defined in the environment file!");
      }
  
      const apiUrl = `${baseUrl}/resendDirectorAdminOtp`;
  
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });
  
      const data = await response.json();
  
      if (response.ok && data.success) {
        setTimer(30);
        setIsResendDisabled(true);
        toast.success(data.message || "OTP resent successfully!");
      } else {
        const errorMessage = data.message || "Failed to resend OTP.";
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Resend OTP failed:", error.message);
      toast.error(error.message);
    }
  };
  
  const handleVerifyOtp = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API;
  
      if (!baseUrl) {
        throw new Error("Base API URL is not defined in the environment file!");
      }
  
      const apiUrl = `${baseUrl}/verifyDirectorAdminOtp`;
  
      const payload = {
        email,
        otp,
        status: "",
        platform: "",
        deviceName: "",
        locationIp: "",
      };

      const token = sessionStorage.getItem("authToken");
      // if (!token) {
      //   throw new Error("Authentication token not found. Please log in again.");
      // }
  
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",         
        },
        body: JSON.stringify(payload),
      });
  
      const data = await response.json();
  
      if (response.ok && data.success) {
      sessionStorage.setItem("authToken", data.token);
        toast.success("OTP verified successfully!");
        setIsOtpPhase(false);
        navigate("/overview");
      } else {
        throw new Error(data.message || "OTP verification failed.");
      }
    } catch (error) {
      console.error("OTP verification failed:", error.message);
      toast.error(error.message);
    }
  };
  

  useEffect(() => {
    let interval;
    if (isOtpPhase && timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsResendDisabled(false);
    }
    return () => clearInterval(interval);
  }, [timer, isOtpPhase]);

  useEffect(() => {
    const timeout = setTimeout(() => setLoading(false), 2000);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");

    if (token) {
      navigate("/dashboard");
    } else {
      setLoading(false);
      navigate("/");   
    }
  }, []);

  return (
    <>
      {loading ? (
        <div className="loading-screen">
          <div className="logo-container">
            <img src="assets/images/BlueAsterAR.png" alt="Blue Aster Logo" />
            <div className="spinner"></div>
          </div>
        </div>
      ) : (
        <section className="auth bg-base d-flex flex-wrap">
          <div className="auth-left d-lg-block d-none">
            <div className="d-flex align-items-center flex-column h-100 justify-content-center">
              <img src="assets/images/auth/auth-img.png" alt="" />
            </div>
          </div>
          <div className="auth-right py-32 px-24 d-flex flex-column justify-content-center">
            <div className="max-w-464-px mx-auto w-100">
              {!isOtpPhase ? (
                <>
                  <div>
                    <Link to="/" className="mb-40 max-w-290-px">
                      <img src="assets/images/BlueAsterAR.png" alt="" />
                    </Link>
                    <h4 className="mb-12">Sign In to your Account</h4>
                    <p className="mb-32 text-secondary-light text-lg">
                      Welcome back! Please enter your details
                    </p>
                  </div>
                  <form onSubmit={handleLogin}>
                    <div className="icon-field mb-16">
                      <span className="icon top-50 translate-middle-y">
                        <Icon icon="mage:email" />
                      </span>
                      <input
                        type="email"
                        className="form-control h-56-px bg-neutral-50 radius-12"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="position-relative mb-20">
                      <div className="icon-field">
                        <span className="icon top-50 translate-middle-y">
                          <Icon icon="solar:lock-password-outline" />
                        </span>
                        <input
                          type="password"
                          className="form-control h-56-px bg-neutral-50 radius-12"
                          placeholder="Password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    <div className="d-flex justify-content-between gap-2">
                      <div className="form-check style-check d-flex align-items-center">
                        <input
                          className="form-check-input border border-neutral-300"
                          type="checkbox"
                          id="remember"
                        />
                        <label className="form-check-label" htmlFor="remember">
                          Remember me
                        </label>
                      </div>
                      {/* <Link to="#" className="text-primary-600 fw-medium">
                        Forgot Password?
                      </Link> */}
                    </div>
                    <button
                      type="submit"
                      className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-32"
                    >
                      Sign In
                    </button>
                    {/* <div className="text-center text-sm">
                    <p className="mb-0">Don't have an account?{" "}
                      <Link to="/sign-up" className="text-primary-600 fw-semibold">
                          Sign Up
                    </Link>
                    </p>
                </div> */}
                  </form>
                </>
              ) : (
                <div>
                  <h4 className="mb-12">Enter OTP</h4>
                  <p className="mb-32 text-secondary-light text-lg">
                    Please enter the OTP sent to your email.
                  </p>
                  <div className="icon-field mb-16">
                    <span className="icon top-50 translate-middle-y">
                      <Icon icon="solar:lock-password-outline" />
                    </span>
                    <input
                      type="text"
                      className="form-control h-56-px bg-neutral-50 radius-12"
                      placeholder="Enter OTP"
                      required
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-32"
                    onClick={handleVerifyOtp}
                  >
                    Verify OTP
                  </button>
                  <div className="mt-16 text-center">
                    {isResendDisabled ? (
                      <p className="text-secondary">
                        Resend OTP in <strong>{timer}s</strong>
                      </p>
                    ) : (
                      <button
                        className="btn btn-link text-primary fw-medium"
                        onClick={handleResendOtp}
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <ToastContainer position="top-right" autoClose={3000} />
        </section>
      )}
    </>
  );
};

export default SignInLayer;
